import { useCallback, useEffect, useState } from "react";
import { Fade } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDeleteEvent, useEventByRole } from "../../hooks/useEvent";
import { useCurrentUser } from "../../hooks/useAuth";
import EventStatusBadge from "../EventDetails/EventStatus";
import DataTable from "../shared/DataTable";
import { formatDate } from "../../utils/helpers";
import { EventStatusConstants } from "../../constants/eventStatus";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { usePermission } from "../../hooks/usePermission";
import { DeleteEventsPermission } from "../../constants/permissions";
import teamService from "../../services/teamService";
import divisionsService from "../../services/divisionService";
import userService from "../../services/userService";
import { ShimmerBadge, ShimmerTable } from "react-shimmer-effects";

export default function EventsTable({
  dateRange,
  selectedEventType,
  selectedDivision,
  selectedTeam,
  selectedSpendType,
  selectedActivityType,
  selectedStatus,
  searchQuery,
  onSelectionChange,
}) {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const { data: eventsData, loading } = useEventByRole({
    user,
    dateRange,
    eventTypeId: selectedEventType || undefined,
    divisionId: selectedDivision || undefined,
    teamId: selectedTeam || undefined,
    spendTypeId: selectedSpendType || undefined,
    activityTypeId: selectedActivityType || undefined,
  });

  const { mutate: deleteEvent } = useDeleteEvent();

  const [deleteModal, setDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);

  const { data: canDeleteEvent } = usePermission({
    permissionName: DeleteEventsPermission,
  });

  const fetchData = useCallback(async () => {
    if (!eventsData) return;

    setIsFetching(true);
    try {
      const eventsWithAdditionalData = await Promise.all(
        eventsData.map(async (event) => {
          const divisionName = (await divisionsService.getAcronymById(event.division_id)) || "";
          const teamName = (await teamService.getNameById(event.team_id)) || "";
          const creatorName = (await userService.getFirstNameFamilyNameById(event.created_by)) || "";

          return {
            ...event,
            division_name: divisionName,
            team_name: teamName,
            creator_name: creatorName,
          };
        })
      );

      const filtered = eventsWithAdditionalData.filter((event) => {
        // Search filtering
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const searchFields = [
            event.id,
            event.name,
            event.promo_number,
            event.division_name,
            event.team_name,
            event.creator_name,
          ];
          if (
            !searchFields.some((field) =>
              String(field || "")
                .toLowerCase()
                .includes(query)
            )
          ) {
            return false;
          }
        }

        // Status filtering
        if (selectedStatus) {
          return event.status === EventStatusConstants[selectedStatus]?.text;
        }

        return true;
      });

      setFilteredData(filtered);
    } finally {
      setIsFetching(false);
    }
  }, [eventsData, searchQuery, selectedStatus]);

  useEffect(() => {
    if (eventsData) fetchData();
  }, [eventsData]);

  const handleRowClick = (row) => {
    navigate(`/event-details/${row.id}`);
  };

  const handleDelete = (event) => {
    setEventToDelete(event);
    setDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (eventToDelete) {
      deleteEvent(eventToDelete.id);
      setDeleteModal(false);
      setEventToDelete(null);
    }
  };

  const columns = [
    {
      name: "Event ID",
      selector: (row) => row.id,
      sortable: true,
      cell: (row) => (
        <span
          className="font-bold cursor-text"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {row.id}
        </span>
      ),
      minWidth: "110px",
    },

    {
      name: "Event Name",
      selector: (row) => row.name,
      sortable: true,
      minWidth: "180px",
    },
    {
      name: "Creator",
      selector: (row) => row.creator_name,
      sortable: true,
      minWidth: "160px",
    },
    {
      name: "Division",
      selector: (row) => row.division_name,
      sortable: true,
      minWidth: "110px",
    },
    {
      name: "Team",
      selector: (row) => row.team_name,
      sortable: true,
      minWidth: "140px",
    },
    {
      name: "Promo Num",
      selector: (row) => row.promo_number,
      sortable: true,
      minWidth: "130px",
    },
    {
      name: "Start Date",
      selector: (row) => new Date(row.start_date),
      sortable: true,
      cell: (row) => formatDate(row.start_date),
      minWidth: "120px",
    },
    {
      name: "Status",
      cell: (row) => <EventStatusBadge status={row.status} size="xs" />,
      minWidth: "80px",
    },
    {
      name: "Actions",
      cell: (row) =>
        canDeleteEvent &&
        row.status !== EventStatusConstants.COMPLETED.text &&
        row.status !== EventStatusConstants.CLOSED.text && (
          <button
            data-track="delete-event-button"
            data-module="Events"
            onClick={(e) => {
              e.stopPropagation(); // Prevent row click when clicking the trash icon
              handleDelete(row);
            }}
            className="text-red-600 hover:text-red-800"
          >
            <i className="fas fa-trash"></i>
          </button>
        ),
      minWidth: "10px",
      center: true,
    },
  ];

  if (loading || isFetching) {
    return (
      <div className="relative min-h-[400px] bg-white rounded-lg shadow-md py-4 px-8">
        <div className="flex justify-end mt-10 mr-5">
          <ShimmerBadge width={250} />
        </div>
        <ShimmerTable row={8} col={8} gap={20} />
      </div>
    );
  }

  return (
    <Fade in={true} timeout={500}>
      <div>
        <div className="bg-white p-4 md:p-6 my-4 rounded-lg">
          <div className="relative min-h-[400px]">
            <div className="overflow-x-auto">
              <DataTable
                columns={columns}
                data={filteredData || []}
                onRowClick={handleRowClick}
                selectableRows
                onSelectedRowsChange={({ selectedRows }) => {
                  const selectedIds = selectedRows.map((row) => row.id);
                  onSelectionChange?.(selectedIds);
                }}
                searchFields={[
                  "id",
                  "name",
                  "promo_number",
                  "division_name",
                  "team_name",
                  "creator_name",
                  "start_date",
                ]}
                searchPlaceholder="Search events..."
                searchQuery={searchQuery}
                customStyles={{
                  table: {
                    style: {
                      minWidth: "100%",
                    },
                  },
                  cells: {
                    style: {
                      whiteSpace: "nowrap",
                      padding: "12px 16px",
                    },
                  },
                  header: {
                    style: {
                      whiteSpace: "nowrap",
                      padding: "12px 16px",
                    },
                  },
                }}
              />
            </div>
          </div>

          <Dialog
            open={deleteModal}
            onClose={() => setDeleteModal(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle
              id="alert-dialog-title"
              className="bg-red-50 text-red-700"
            >
              Confirm Deletion
            </DialogTitle>
            <DialogContent className="mt-10 mb-4">
              <DialogContentText id="alert-dialog-description">
                Are you sure you want to delete {eventToDelete?.name} Event?
              </DialogContentText>
            </DialogContent>
            <DialogActions className="px-4">
              <button
                onClick={() => setDeleteModal(false)}
                className="px-4 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 hover:rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Delete
              </button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </Fade>
  );
}